import { SelectChangeEvent } from '@mui/material'
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { executeGetReductions } from '../../dataAccess/webApi/dao/reductionsDao'
import { fromApiYmd } from '../../utils/dateUtil'
import { nullPropsToUndefined } from '../../utils/objectUtil'
import { getCategories, getCategorySpecifyMasters, SpecifyMasterType } from '../common/category'
import { ADDITIONALS, Child, ChildrenChoice, getChild, getChildrenChoices } from '../common/child'
import { memberAddChildUrl, memberModifyUserUrl } from '../common/constant/appUrl'
import { yesNo } from '../common/constant/classification'
import { OperationId } from '../common/constant/operationLog'
import { useErrorHandle } from '../common/error/errorHandler'
import { useOperationLog } from '../common/operationLog'
import { showLoading } from '../common/store/slices/application'
import { selectIsVerifiedAll } from '../common/store/slices/authority'
import { User, getUser } from '../common/user'
import { selectSystemControl } from '../common/store/slices/systemControl'
import { GetCategoryMstDto } from '../../dataAccess/webApi/dto/categoryMstDto'
import { CATEGORY_NAME as ALLERGY } from '../../views/components/common/member/childAllergyUnit'
import { CATEGORY_NAMES as BIRTH_KEYS } from '../../views/components/common/member/childBirthUnit'
import { CATEGORY_NAMES as HEALTH_KEYS } from '../../views/components/common/member/childHealthUnit'
import { CATEGORY_NAMES as FAMILY_HOSPITAL_KEYS } from '../../views/components/common/member/childFamilyHospitalUnit'
import { CATEGORY_NAME as FEBRILE_SEIZURE } from '../../views/components/common/member/childFebrileSeizureUnit'
import { CATEGORY_NAMES as HOSPITAL_ADMISSION_KEYS } from '../../views/components/common/member/childHospitalAdmissionUnit'
import { CATEGORY_NAME as MEDICAL_HISTORY } from '../../views/components/common/member/childMedicalHistoryUnit'
import { CATEGORY_NAMES as NORMALLY_USED_FACILITY_KEYS } from '../../views/components/common/member/childNormallyUsedFacilityUnit'
import { CATEGORY_NAMES as USUALLY_MEDICINES_KEYS } from '../../views/components/common/member/childUsuallyMedicinesUnit'
import { CATEGORY_NAMES as DAILY_RHYTHM_KEYS } from '../../views/components/common/member/childDailyRhythmUnit'
import { CATEGORY_NAMES as CARE_EXPERIENCE_KEYS } from '../../views/components/common/member/childCareExperienceUnit'
import { CATEGORY_NAME as DISEASE } from '../../views/components/common/member/childDiseaseUnit'
import { CATEGORY_NAME as VACCINATION } from '../../views/components/common/member/childVaccinationUnit'
import { CATEGORY_NAMES as EATING_KEYS } from '../../views/components/common/member/childEatingUnit'
import { CATEGORY_NAME as SYMPTOM } from '../../views/components/common/member/childSymptomUnit'
import { CATEGORY_NAMES as PREDISPOSITION_KEYS } from '../../views/components/common/member/childPredispositionUnit'
import { CATEGORY_NAMES as DISEASE_TEST_KEYS } from '../../views/components/common/member/childDiseaseTestUnit'
import { CATEGORY_NAMES as TREATMENT_KEYS } from '../../views/components/common/member/childTreatmentUnit'
import { PostDecodeMstDto } from '../../dataAccess/webApi/dto/decodeMstDto'
import { getDecodeMstValue } from '../common/getDecodeMstValue'
import { USER_EMERGENCY_CONTACT1_CATEGORY_KEY, USER_EMERGENCY_CONTACT2_CATEGORY_KEY, USER_EMERGENCY_CONTACT3_CATEGORY_KEY, USER_PARENT_CATEGORY_KEY, USER_SPOUSE_CATEGORY_KEY } from '../../views/components/common/member/userContent'
import { CATEGORY_NAMES as DEVELOPMENT_KEYS } from '../../views/components/common/member/childDevelopmentUnit'
import { CATEGORY_NAMES as EXCRETIONS_KEYS } from '../../views/components/common/member/childExcretionsUnit'

interface LocationState {
  /** アクティブなタブの値 */
  tabValue?: string
  /** アクティブなお子さまID */
  childId?: string
}

interface PageState {
  activeTabValue: string
  activeChildId?: string
  user?: User
  child?: Child
  childs?: ChildrenChoice[]
}
interface DialogState {
  reductionName: string
  reductionInfos: {
    status: string
    reductionStartDate?: Date
    reductionEndDate?: Date
  }[]
}

const DECODE_CATEGORYS = {
  /** 生活のリズム	午睡時間 */
  napTime: 'nap_time',
  /** 生活のリズム	寝かしつけ */
  putToSleep: 'put_to_sleep',
  /** 生活のリズム	寝つき */
  fallAsleep: 'fall_asleep',
  /** 生活のリズム	排泄 */
  excretion: 'excretion',
  /** 預かりの経験	預かりの経験先 */
  leaveChildTo: 'leave_child_to',
}

export interface MemberDecodeMst {
  napTime: PostDecodeMstDto[]
  putToSleep: PostDecodeMstDto[]
  fallAsleep: PostDecodeMstDto[]
  excretion: PostDecodeMstDto[]
  leaveChildTo: PostDecodeMstDto[]
}

/** タブの選択値 */
export const tabValue = {
  user: '1',
  child: '2',
} as const

const DEFAULT_PROJECT_ID = '0' // 市民用の項目は事業ID'0'
const DEFAULT_FACILITY_ID = '0' // 市民側では事業共通情報として、facility_id = 0でchildren_detailを扱う

export const useAction = () => {
  /** 使用する項目 */
  const CATEGORY_KEYS = [
    'childrenGender',
    'childrenBloodType',
    'maternalHandbookNo',
    'childrenAddress',
    'allergyFlag',
    'allergyCauses1',
    'allergySymptoms1',
    'allergyTreatments1',
    'allergyConcerns1',
    'allergyCauses2',
    'allergySymptoms2',
    'allergyTreatments2',
    'allergyConcerns2',
    'allergyCauses3',
    'allergySymptoms3',
    'allergyTreatments3',
    'allergyConcerns3',
    'brothersSistersFlag',
    'multipleBirthsFlag',
    'spouse'
  ]
  
  const errorHandle = useErrorHandle()
  const dispatch = useDispatch()
  const history = useHistory<LocationState | undefined>()
  const { addOperationLog } = useOperationLog()
  const sysCtrl = useSelector(selectSystemControl)

  const isVerifiedAll = useSelector(selectIsVerifiedAll)

  const locationState = history.location.state
  const [isOpenReductionsHistory, setIsOpenReductionsHistory] = useState(false)
  const [state, setState] = useState<PageState>({
    activeTabValue: locationState?.tabValue ?? tabValue.user,
    activeChildId: locationState?.childId,
  })
  /** カテゴリーマスタをオブジェクト形式で保持する */
  const [category, setCategory] = useState<GetCategoryMstDto[]>()
  const [categoryMap, setCategoryMap] = useState<{[key:string]: GetCategoryMstDto}>({})
  const [medicalHistoryCategory, setMedicalHistoryCategory] = useState<GetCategoryMstDto[]>()
  const [diseaseCategory, setDiseaseCategory] = useState<GetCategoryMstDto[]>()
  const [vaccinationCategory, setVaccinationCategory] = useState<GetCategoryMstDto[]>()
  const [symptomCategory, setSymptomCategory] = useState<GetCategoryMstDto[]>()
  
  const [reductionHistoryState, setReductionHistoryState] = useState<DialogState>()
  const [decodeMst, setDecodeMst] = useState<MemberDecodeMst | null>(
    null
  )

  const load = useCallback(
    (
      isInitialize: boolean,
      activeTabValue: string,
      loadedChilds?: { value: string; label: string }[],
      activeChildId?: string
    ) => {
      dispatch(
        showLoading({
          process: errorHandle(async () => {
            const [
              category, 
              medicalHistoryCategory,
              diseaseCategory,
              vaccinationCategory,
              symptomCategory,
              getDecodeMstValues
            ] = await Promise.all([
              getCategories({projectId: DEFAULT_PROJECT_ID, categories: [
                ...CATEGORY_KEYS,
                ...USER_PARENT_CATEGORY_KEY,
                ...USER_SPOUSE_CATEGORY_KEY,
                ...USER_EMERGENCY_CONTACT1_CATEGORY_KEY,
                ...USER_EMERGENCY_CONTACT2_CATEGORY_KEY,
                ...USER_EMERGENCY_CONTACT3_CATEGORY_KEY,
                ALLERGY,
                ...BIRTH_KEYS,
                ...HEALTH_KEYS,
                ...FAMILY_HOSPITAL_KEYS,
                FEBRILE_SEIZURE,
                ...HOSPITAL_ADMISSION_KEYS,
                ...EATING_KEYS,
                ...NORMALLY_USED_FACILITY_KEYS,
                ...PREDISPOSITION_KEYS,
                ...USUALLY_MEDICINES_KEYS,
                ...DAILY_RHYTHM_KEYS,
                ...CARE_EXPERIENCE_KEYS,
                ...DISEASE_TEST_KEYS,
                ...TREATMENT_KEYS,
                ...DEVELOPMENT_KEYS,
                ...EXCRETIONS_KEYS
              ]}),
              getCategorySpecifyMasters({projectId: DEFAULT_PROJECT_ID, category: MEDICAL_HISTORY, masterType: SpecifyMasterType.MEDICAL_HISTORY}),
              getCategorySpecifyMasters({projectId: DEFAULT_PROJECT_ID, category: DISEASE, masterType: SpecifyMasterType.MEDICAL_HISTORY}),
              getCategorySpecifyMasters({projectId: DEFAULT_PROJECT_ID, category: VACCINATION, masterType: SpecifyMasterType.VACCINATION}),
              getCategorySpecifyMasters({projectId: DEFAULT_PROJECT_ID, category: SYMPTOM, masterType: SpecifyMasterType.SYMPTOM}),
              getDecodeMstValue(DECODE_CATEGORYS)
            ])

            // ラベル表示に必要な情報をdecode_mstから取得する
            if (getDecodeMstValues) {
              setDecodeMst(getDecodeMstValues)
            }

            // 項目マスタ            
            setCategory(category)
            const tmpMap: {[key:string]: GetCategoryMstDto} = {}
            category?.forEach(e => tmpMap[e.category] = e)
            setCategoryMap(tmpMap)
            // 既往症項目
            setMedicalHistoryCategory(medicalHistoryCategory)
            // 病気項目
            setDiseaseCategory(diseaseCategory)
            // 予防接種項目
            setVaccinationCategory(vaccinationCategory)
            // 症状項目
            setSymptomCategory(symptomCategory)
            
            let childs
            let childId
            let user
            let child
            if (activeTabValue === tabValue.user) {
              childs = loadedChilds
              childId = activeChildId
              user = await getUser()
            } else {
              childs = loadedChilds == null ? await getChildrenChoices() : loadedChilds
              childId = activeChildId ?? childs[0].value
              child = await getChild(childId, DEFAULT_FACILITY_ID, [ADDITIONALS.HOSPITAL_ADMISSION,ADDITIONALS.EATING,ADDITIONALS.PREDISPOSITION,ADDITIONALS.DISEASE_TEST,ADDITIONALS.TREATMENT,ADDITIONALS.DEVELOPMENT,ADDITIONALS.EXCRETIONS]) 
            }

            setState({
              activeTabValue,
              activeChildId: childId,
              user,
              child,
              childs,
            })
          }),
          isHiddenMain: isInitialize,
        })
      )
    },
    []
  )

  // ちらつき防止のためにuseLayoutEffect使用
  useLayoutEffect(() => {
    addOperationLog({ operationId: OperationId.OP_00000001, accessData: [{ userIdRegFlag: yesNo.yes }] })

    load(true, state.activeTabValue, state.childs, state.activeChildId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // 戻るで表示した際にアクティブな選択を復元する為に履歴に保管
    history.replace({ ...history.location, state: { tabValue: state.activeTabValue, childId: state.activeChildId } })
  }, [state.activeTabValue, state.activeChildId])

  const changeTab = useCallback(
    (value: string) => {
      if (value === tabValue.user) {
        addOperationLog({ operationId: OperationId.OP_00000052 })
      } else if (value === tabValue.child) {
        addOperationLog({ operationId: OperationId.OP_00000053 })
      }

      load(false, value, state.childs, state.activeChildId)
    },
    [state.childs, state.activeChildId, addOperationLog]
  )

  const changeChild = useCallback(
    (event: SelectChangeEvent<string>) => {
      addOperationLog({ operationId: OperationId.OP_00000028 })

      load(false, state.activeTabValue, state.childs, event.target.value)
    },
    [state.activeTabValue, state.childs, addOperationLog]
  )

  const goModify = useCallback(() => {
    addOperationLog({ operationId: OperationId.OP_00000057 })

    if (state.activeTabValue === tabValue.user) {
      history.push(memberModifyUserUrl.url())
    }
  }, [state.activeTabValue, state.activeChildId, addOperationLog])

  const goAddChild = useCallback(() => {
    history.push(memberAddChildUrl.url())
  }, [history])

  const onClickReductionHistories = useCallback(
    (id: string) => {
      addOperationLog({ operationId: OperationId.OP_00000054 })

      dispatch(
        showLoading({
          process: errorHandle(async () => {
            const result = await getReductionHistories(id)
            setReductionHistoryState(result)
            setIsOpenReductionsHistory(true)
          }),
          isHiddenMain: false,
        })
      )
    },
    [addOperationLog]
  )

  const onCloseReductionsHistory = useCallback(() => {
    addOperationLog({ operationId: OperationId.OP_00000055 })

    setIsOpenReductionsHistory(false)
  }, [addOperationLog])

  const getReductionHistories = async (id: string) => {
    const response = await executeGetReductions(id)
    const reductionInfos = response.result.reductionInfos.map((v) => {
      return nullPropsToUndefined({
        status: v.status,
        reductionStartDate: fromApiYmd(v.reductionStartDate),
        reductionEndDate: fromApiYmd(v.reductionEndDate),
      })
    })

    return nullPropsToUndefined({
      reductionName: response.result.reductionName,
      reductionInfos: reductionInfos,
    })
  }
  return {
    isVerifiedAll,
    activeTabValue: state.activeTabValue,
    activeChildId: state.activeChildId,
    childs: state.childs,
    user: state.user,
    child: state.child,
    isOpenReductionsHistory,
    reductionHistoryState,
    changeTab,
    changeChild,
    goModify,
    goAddChild,
    onClickReductionHistories,
    onCloseReductionsHistory,
    sysCtrl,
    category,
    categoryMap,
    medicalHistoryCategory,
    diseaseCategory,
    vaccinationCategory,
    symptomCategory,
    decodeMst
  }
}